import { useEffect, useMemo, useState } from "react";
import { Button } from "../../components/Button";
import { Flex } from "../../components/Flex";
import Topbar from "../../components/Topbar";
import { ROUTE } from "../../routes";
import { Breadcrumb } from "../../components/Breadcrumbs";
import { Modal } from "../../components/Modal";
import { useNavigate, useParams } from "react-router-dom";
import { Comments } from "../../components/Comments";
import { Card } from "../../components/Card";
import { H3 } from "../../components/Heading";
import { Box } from "../../components/Box";
import { DetailField } from "../../components/DetailField";
import { IconButtonDropdown } from "../../components/IconButtonDropdown";
import { IconButtonDropdownOption } from "../../components/IconButtonDropdown/IconButtonDropdownOption";
import { VscDebugRestart } from "react-icons/vsc";
import resendInviteIcon from "../../assets/resend-invite-icon.svg";
import commentsIcon from "../../assets/comments-icon.svg";
import downloadIcon from "../../assets/download-arrow.svg";
import { Image } from "../../components/Image";
import { FaTrash } from "react-icons/fa";
import { ActionItem } from "./ActionItem";
import { ProgressBar } from "../../components/ProgressBar";
import { Paragraph } from "../../components/Paragraph";
import { calculatePercentage } from "../../utils/number";
import { useIndividual } from "../../data/useIndividual";
import { useCreateIndividualsInvites } from "../../data/useCreateIndividualsInvites";
import { clearAll, notify } from "../../utils/notify";
import { handleQueryError, showError } from "../../utils/error-handling";
import { useMandate } from "../../data/useMandate";
import { capitalise } from "../../utils/string";
import { DateTime } from "luxon";
import { queryClient } from "../../queryClient";
import { CheckSection } from "./CheckSection";
import { Loader } from "../../components/Loader";
import { useDeleteIndividual } from "../../data/useDeleteIndividual";
import { useDeleteMandate } from "../../data/useDeleteMandate";
import {
  MandateStatus,
  useMandateStatuses,
} from "../../context/MandateStatusContext";
import { useAuth } from "../../context/AuthContext";
import { axios } from "../../data/axios";
import { env } from "../../utils/env";
import theme from "../../theme";
import { checksColor, checksIcons } from "./helper";
import { useAdditionalDocuments } from "../../data/useAdditionalDocuments";
import { AdditionalDocuments } from "../../components/AdditionalDocuments";
import { BiCopy } from "react-icons/bi";
import { IconButton } from "../../components/IconButton";
import { IndividualDetailsView } from "./IndividualDetaisView";
import { MandateTypeForm } from "../../types/Forms";
import { MandateFormList } from "../../components/MandateFormList";
import { NewDocumentsRequest } from "./NewDocumentsRequest";
import { IndividualAdditionalDocumentType } from "../../types/AdditionalDocumentsType";
import { useGetExtraAction } from "../../data/useGetExtraAction";
import { ProfileRecordWidget } from "../../components/ProfileRecordWidget";
import { RecordSharing } from "../../components/RecordAccess";
import { useCountries } from "../../data/useCountries";
import { useEntityTypes } from "../../data/useEntityTypes";
import { Country } from "../../types/Country";
import { EntityType } from "../../types/CompanyType";
import {
  Heading,
  TitleHeading,
  Container,
  ChecksBar,
  ViewLink,
  CountBox,
  CardLine,
} from "./styles";
import { PersonOverviewTab } from "./PersonOverviewTab";
import { EntityOverviewTab } from "./EntityOverviewTab";

export { Heading, TitleHeading, Container, ChecksBar, ViewLink, CountBox, CardLine };

const toId = (v: any): number | null => {
  if (typeof v === "number") return v;
  if (v && typeof v === "object" && typeof v.id === "number") return v.id;
  return null;
};

export const IndividualView = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { authHeader, hasPermission } = useAuth();

  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [isViewDocumentsOpen, setIsViewDocumentsOpen] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [isRequestingPdfDownload, setIsRequestingPdfDownload] = useState(false);
  const [isLoadingReset, setIsLoadingReset] = useState(false);
  // const [isViewMore] = useState(false);
  // const [showContent, setShowContent] = useState(isViewMore);
  const [isAllDetailsOpen, setIsAllDetailsOpen] = useState(false);
  const [showAddNewForm, setShowAddNewForm] = useState(false);
  const [isSelectAdditionalDocumentOpen, setIsSelectAdditionalDocumentOpen] =
    useState(false);

  // useEffect(() => {
  //   if (isViewMore) {
  //     setShowContent(true);
  //   } else {
  //     const timer = setTimeout(() => setShowContent(false), 500); // Duration of the transition
  //     return () => clearTimeout(timer);
  //   }
  // }, [isViewMore, isAllDetailsOpen])

  const { mutate: createIndividualsInvites, isLoading: isInvitingUser } =
    useCreateIndividualsInvites();

  const { data: individualData, isLoading } = useIndividual(params.id);

  const individual = individualData?.data;

  const { data: countriesData } = useCountries();
  const { data: entityTypesData } = useEntityTypes();

  const countryList: Country[] = useMemo(
    () => countriesData?.data ?? [],
    [countriesData?.data],
  );
  const entityTypeList: EntityType[] = useMemo(
    () => entityTypesData?.data ?? [],
    [entityTypesData?.data],
  );

  const entityDetailCountryName = useMemo(() => {
    const id = toId(individual?.personal_detail?.country);
    return countryList.find((c) => c.id === id)?.name ?? "-";
  }, [countryList, individual?.personal_detail?.country]);

  const entityDetailTypeName = useMemo(() => {
    const id = toId(individual?.personal_detail?.entity_type);
    return entityTypeList.find((t) => t.id === id)?.name ?? "-";
  }, [entityTypeList, individual?.personal_detail?.entity_type]);

  const individualDisplayName = [
    individual?.first_name,
    individual?.middle_name,
    individual?.last_name,
  ]
    .filter(Boolean)
    .join(" ");

  const individualName = isLoading
    ? "..."
    : individualDisplayName ||
      individual?.company_name ||
      individual?.profile?.name ||
      "-";

  const { mutate: deleteIndividual } = useDeleteIndividual();
  const { mutate: deleteMandate } = useDeleteMandate();

  const { getMandateStatusId } = useMandateStatuses();
  const { data: mandateData, isLoading: isLoadingMandate } = useMandate(
    individual?.mandate,
  );

  const { data: additionalDocuments, isLoading: documentsLoading } =
    useAdditionalDocuments(params.id);

  const individualId = individual?.id;

  const { data: extraActionList } = useGetExtraAction(individualId);

  const mandate = mandateData?.data;
  const mandateReference = isLoadingMandate ? "..." : mandate?.name;
  const formRequestType: "individual" | "entity" = individual?.company_name
    ? "entity"
    : "individual";

  const [formsData, setFormsData] = useState<MandateTypeForm[]>([]);

  const [additionalDocumentsData, setAdditionalDocumentsData] = useState<
    IndividualAdditionalDocumentType[]
  >([]);

  useEffect(() => {
    if (extraActionList?.data?.form) {
      setFormsData(extraActionList?.data?.form);
    }
    if (extraActionList?.data?.additional_document) {
      setAdditionalDocumentsData(extraActionList?.data?.additional_document);
    }
  }, [extraActionList]);

  const handleResendInvite = () => {
    createIndividualsInvites(
      {
        individual_ids: [parseInt(params.id)],
        id: individual?.mandate,
      },
      {
        onSuccess: () => {
          notify("Invite successfully sent", {
            position: "bottom-left",
            type: "success",
          });
          queryClient.invalidateQueries(["individual", params.id]);
        },
        onError: (err: any) => {
          showError(err, "A problem occurred while send your message.");
        },
      },
    );
  };

  const handleDownloadPdfClick = () => {
    if (isRequestingPdfDownload) {
      return;
    }

    setIsRequestingPdfDownload(true);
    notify("Generating PDF, this could take up to 30 seconds", {
      position: "bottom-left",
      type: "info",
      pauseOnFocusLoss: false,
      pauseOnHover: false,
      autoClose: false,
    });

    axios
      .get(`${env.verifyServiceUrl}individual/download_pdf/${params.id}`, {
        headers: {
          Authorization: authHeader,
        },
        responseType: "arraybuffer",
        skipGlobalErrorHandler: true,
      })
      .then((response) => {
        clearAll();
        const blob = new Blob([response.data], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        const timestamp = DateTime.now().toFormat("yyyy-MM-dd_HH-mm-ss");

        const reportName =
          individualDisplayName ||
          individual?.company_name ||
          individual?.profile?.name ||
          "-";

        link.download = `Customer Report - ${individual?.reference} - ${reportName} - ${timestamp}.pdf`;
        link.click();
      })
      .catch(() => {
        clearAll();
        setTimeout(() => {
          notify("There was a problem while generating the PDF report", {
            position: "bottom-left",
            type: "error",
          });
        }, 100);
      })
      .finally(() => {
        setIsRequestingPdfDownload(false);
      });
  };

  const handleResetIndividual = () => {
    setIsLoadingReset(true);
    axios
      .get(`${env.verifyServiceUrl}individual/individual/${params.id}/reset/`, {
        headers: {
          Authorization: authHeader,
        },
      })
      .then((response) => {
        if (response.status === 200) {
          notify("Individual reset successfully.", {
            position: "bottom-left",
            type: "success",
          });
          queryClient.invalidateQueries(["mandate", mandate?.id]);
          queryClient.invalidateQueries(["individual", params.id]);
          queryClient.invalidateQueries(["individual"]);
          queryClient.invalidateQueries(["extra_action", individualId]);
          setFormsData([]);
          setAdditionalDocumentsData([]);
          setIsLoadingReset(false);
          setIsResetModalOpen(false);
        } else {
          setIsLoadingReset(false);
          handleQueryError(
            "A problem occurred while resetting the individual.",
          );
        }
      });
  };

  const handleConfirmDeleteIndividual = () => {
    setIsConfirmDeleteOpen(false);
    if (formRequestType === "entity") {
      deleteMandate(
        { id: individual?.mandate },
        {
          onSuccess: () => {
            navigate(ROUTE.APPLICATIONS);
            notify("Application deleted", {
              position: "bottom-left",
              type: "success",
            });
          },
          onError: (err: any) => {
            showError(
              err,
              "A problem occurred while deleting the application.",
            );
          },
        },
      );
      return;
    }

    deleteIndividual(
      { id: params.id },
      {
        onSuccess: () => {
          navigate(`/applications/${mandate?.id}`);
          notify("Individual removed", {
            position: "bottom-left",
            type: "success",
          });
          queryClient.invalidateQueries(["mandate", mandate?.id]);
          // queryClient.invalidateQueries(["individual"]);
        },
        onError: (err: any) => {
          showError(err, "A problem occurred while deleting the individual.");
        },
        // onError: handleQueryError(
        //   "A problem occurred while deleting the individual."
        // ),
      },
    );
  };

  const actions = {
    countComplete: individual?.completed_actions?.length || 0,
    count: individual?.total_actions?.length || 0,
    completed: individual?.completed_actions || [],
    total: individual?.total_actions || [],
  };
  // const { data: monitoringList } = useMonitoringList(individual?.reference);
  // const monitoringIcons = {
  //   active: success,
  //   "requires review": inReview,
  //   pending: pending,
  // };

  const hasInviteBeenSent = Boolean(individual?.invite_sent);
  const inviteDate = DateTime.fromISO(individual?.invite_sent).toFormat(
    "dd/MM/yyyy",
  );
  const inviteTime = DateTime.fromISO(individual?.invite_sent).toFormat(
    "h:mma",
  );

  const mandateReferenceBreadcrumbLength = mandateReference?.length;

  const canDelete =
    mandate?.status?.id !== getMandateStatusId(MandateStatus.Rejected);

  const handleClose = () => {
    // refetch();

    setShowAddNewForm(false);
  };

  return (
    <div>
      <Topbar
        header={
          <Flex justifyContent="space-between" width="100%">
            <>
              <Breadcrumb>
                <Breadcrumb.Section linkTo={ROUTE.APPLICATIONS}>
                  Applications
                </Breadcrumb.Section>
                <Breadcrumb.Section
                  linkTo={`/applications/${individual?.mandate}`}
                >
                  {mandateReferenceBreadcrumbLength > 25
                    ? mandateReference.substring(0, 25) + "..."
                    : mandateReference}
                </Breadcrumb.Section>
                <Breadcrumb.Section>{individualName}</Breadcrumb.Section>
              </Breadcrumb>
              <Flex justifyContent="space-between">
                {/* A person under an application has no tagging of their
                    own — sharing acts on the parent mandate. */}
                <Box mr={1}>
                  <RecordSharing
                    module="application"
                    recordId={mandate?.id}
                    recordName={mandate?.name ?? "-"}
                    recordReference={mandate?.reference}
                    noun="application"
                    primaryDivision={mandate?.primary_division}
                    primaryTeam={mandate?.primary_team}
                    additionalDivisions={mandate?.additional_divisions}
                    additionalTeams={mandate?.additional_teams}
                    invalidateKeys={[["mandate", mandate?.id]]}
                  />
                </Box>
                <Button
                  onClick={() => setIsCommentsOpen(!isCommentsOpen)}
                  mr={1}
                  variant="secondary"
                >
                  <Image
                    src={commentsIcon}
                    alt="comments"
                    height="auto"
                    width="22px"
                    mr={1}
                  />
                  Comments
                </Button>

                {individual?.can_download_pdf && (
                  <Button
                    onClick={handleDownloadPdfClick}
                    isDisabled={
                      isRequestingPdfDownload ||
                      !hasPermission("individual.downloadpdf")
                    }
                    title={
                      hasPermission("individual.downloadpdf")
                        ? undefined
                        : "You don't have permission to download this record as a PDF"
                    }
                    variant="secondary"
                    color={theme.colors.gray[80]}
                    mr={1}
                  >
                    <Image
                      src={downloadIcon}
                      alt="download pdf"
                      height="auto"
                      width="22px"
                      mr={1}
                    />
                    <Paragraph fontWeight="500" fontSize={theme.fontSizes[1]}>
                      {isRequestingPdfDownload
                        ? "Downloading..."
                        : "Download PDF"}
                    </Paragraph>
                  </Button>
                )}

                {canDelete || individual?.can_download_pdf ? (
                  <IconButtonDropdown placement="bottom-end">
                    <IconButtonDropdownOption
                      onClick={
                        hasPermission("individual.reset")
                          ? () => setIsResetModalOpen(true)
                          : undefined
                      }
                      isDisabled={!hasPermission("individual.reset")}
                      title={
                        hasPermission("individual.reset")
                          ? undefined
                          : "You don't have permission to reset this record"
                      }
                      IconType={VscDebugRestart}
                    >
                      Reset
                    </IconButtonDropdownOption>

                    {canDelete && (
                      <IconButtonDropdownOption
                        onClick={() => setIsConfirmDeleteOpen(true)}
                        IconType={FaTrash}
                        isDestructive
                      >
                        Delete
                      </IconButtonDropdownOption>
                    )}
                  </IconButtonDropdown>
                ) : null}
              </Flex>
            </>
          </Flex>
        }
      ></Topbar>

      {isLoading && (
        <Flex justifyContent="center" alignItems="center" minHeight="60vh">
          <Loader />
        </Flex>
      )}

      {!isLoading && (
        <>
          <Container>
            <Flex gap="24">
              <Box width={"20%"}>
                <H3 mb={2}>Progress </H3>

                <Card mb={2}>
                  <Flex justifyContent={"space-between"} alignItems={"center"}>
                    <Heading>Uploaded documents</Heading>

                    <CountBox>{additionalDocuments?.data.total}</CountBox>
                  </Flex>
                  <CardLine mb={3}>
                    View and download uploaded documents
                  </CardLine>
                  <Flex justifyContent={"space-between"}>
                    <Button
                      variant="gray"
                      padding={2}
                      backgroundColor="gray.10"
                      border="2px solid background"
                      color="blue.primary"
                      fontWeight={600}
                      onClick={() => setIsSelectAdditionalDocumentOpen(true)}
                    >
                      Request New
                    </Button>
                    <Button
                      variant="gray"
                      padding={2}
                      backgroundColor="gray.10"
                      border="2px solid background"
                      color="blue.primary"
                      fontWeight={600}
                      onClick={() =>
                        setIsViewDocumentsOpen(!isViewDocumentsOpen)
                      }
                    >
                      View
                    </Button>
                  </Flex>
                </Card>

                <Card mb={0}>
                  <ProfileRecordWidget
                    record={individual}
                    recordId={individual?.id}
                    recordType="application"
                    subjectType={
                      individual?.company_name || individual?.profile
                        ? "entity"
                        : "individual"
                    }
                  />
                  <Heading>Our reference</Heading>
                  <Flex mt={1}>
                    <Paragraph mt={0} color="gray.50" mr={2}>
                      {individual?.reference}
                    </Paragraph>
                    <IconButton
                      pt={0}
                      onClick={() => {
                        navigator.clipboard.writeText(individual?.reference);
                        notify("Copied to clipboard", {
                          type: "success",
                          position: "bottom-left",
                        });
                      }}
                      Type={BiCopy}
                      size="23px"
                    />
                  </Flex>
                  <DetailField
                    mt={2}
                    mb={3}
                    label="Invite sent to"
                    value={individual?.email}
                  />
                  <DetailField
                    label="Invitation Time"
                    value={
                      hasInviteBeenSent
                        ? `${inviteDate} at ${inviteTime}`
                        : "Never"
                    }
                  />

                  <Button
                    marginBottom={3}
                    mt={3}
                    variant="gray"
                    paddingLeft={2}
                    backgroundColor="gray.10"
                    border="2px solid background"
                    color="blue.primary"
                    fontWeight={600}
                    isDisabled={
                      isInvitingUser ||
                      mandate?.status?.status === MandateStatus.Completed ||
                      mandate?.status?.status === MandateStatus.Rejected ||
                      mandate?.archived
                    }
                    onClick={handleResendInvite}
                  >
                    <Image
                      src={resendInviteIcon}
                      alt="progress indicator"
                      height="auto"
                      width="22px"
                      mr={1}
                    />
                    Resend invite
                  </Button>

                  {/* <Heading>Monitoring record</Heading>
                  {monitoringList?.data?.count > 0 && (
                    <div>
                      <Flex mt={3}>
                        <Image
                          src={monitoringIcons[monitoringList?.data?.results[0]?.status.status]}
                          alt="checks"
                          height="20px"
                          width="24px"
                        />
                        <Paragraph color="gray.70" my="0px" ml={1}>
                        {monitoringList?.data?.results[0]?.status.status}
                        </Paragraph>
                      </Flex>
                      <Button
                        mb={3}
                        mr={1}
                        mt={2}
                        variant="gray"
                        paddingLeft={2}
                        backgroundColor="gray.10"
                        border="2px solid background"
                        color="blue.primary"
                        fontWeight={600}
                        size="regular"
                        onClick={() =>
                          navigate(`/monitoring/${monitoringList?.data?.results[0].id}`, {
                          })
                        }
                      >
                        View Monitoring
                      </Button>
                    </div>

                  )}

                  {monitoringList?.data?.count == 0  && (
                    <Button
                      mb={3}
                      onClick={() =>
                        navigate(`/applications/activate-monitoring-individual/${individual?.id}`, {
                          state: { id: individual?.id, mandateId: individual?.mandate },
                        })
                      }
                      mr={1}
                      variant="gray"
                      paddingLeft={2}
                      backgroundColor="gray.10"
                      border="2px solid background"
                      color="blue.primary"
                      fontWeight={600}
                      isDisabled= {individual.checks.pending.length > 0}
                    >
                      <Image
                        src={monitoringIcon}
                        alt="Activate monitoring"
                        height="auto"
                        width="22px"
                        mr={1}
                      />
                      Activate Monitoring
                    </Button>
                  )} */}

                  <Heading>Actions</Heading>
                  <Paragraph fontSize={1} color="gray.60" mb={0}>
                    {actions.countComplete}/{actions.count} Completed
                  </Paragraph>
                  <ProgressBar
                    progress={calculatePercentage(
                      actions.countComplete,
                      actions.count,
                    )}
                    variant="green"
                    mb={2}
                    mt={1}
                  />

                  {actions.total.map(({ id, name, title }, index) => (
                    <ActionItem
                      key={index}
                      label={title}
                      isComplete={actions.completed.find(
                        ({ id: thisId }) => id === thisId,
                      )}
                    />
                  ))}
                </Card>
              </Box>

              <Box width={"80%"}>
                <Box>
                  <Flex justifyContent={"space-between"}>
                    <H3 mb={2}>About</H3>
                  </Flex>

                  <Card mb={2}>
                    <Flex gap="12">
                      <Box flex={1} position={"relative"}>
                        {/* <Flex justifyContent="space-between"> */}
                        {individual && individual?.first_name ? (
                          <PersonOverviewTab
                            individual={individual}
                            setShowAddNewForm={setShowAddNewForm}
                          />
                        ) : (
                          <EntityOverviewTab
                            individual={individual}
                            setShowAddNewForm={setShowAddNewForm}
                            entityDetailCountryName={entityDetailCountryName}
                            entityDetailTypeName={entityDetailTypeName}
                          />
                        )}
                        <Box position={"absolute"} right={0} top={0}>
                          <ViewLink
                            onClick={() =>
                              setIsAllDetailsOpen(!isAllDetailsOpen)
                            }
                            backgroundColor="white"
                            color="black"
                            border={"none"}
                            fontWeight={400}
                            fontSize={16}
                            padding={0}
                          >
                            All Details
                          </ViewLink>
                        </Box>
                        {/* </Flex> */}
                      </Box>
                    </Flex>
                    {/* <div className={`${isViewMore ? "expanded" : ""}`}>
                      {showContent && (
                        <div>
                          <hr style={{ borderColor: "gray.20" }}></hr>
                          <Flex gap="24">
                            <Box
                              flex={1}
                              borderRight="1px solid"
                              borderColor="gray.20"
                            >
                            </Box>


                          </Flex>
                        </div>
                      )}
                    </div> */}
                  </Card>
                </Box>
                <H3 mb={2}>Checks</H3>

                <Flex gap="40" flexWrap={"wrap"}>
                  {individual?.checks &&
                    Object.entries(individual?.checks).map(
                      ([key, check], index) => (
                        <Box flex={1} key={index}>
                          <Flex justifyContent="space-between">
                            <H3 mb="0px">
                              <Flex alignItems="center" gap="8">
                                <Image
                                  src={checksIcons[key]}
                                  alt="icon"
                                  height="20px"
                                  width="auto"
                                  mr={1}
                                />
                                {capitalise(key)}
                              </Flex>
                            </H3>
                            <Heading color="gray.50">{check?.length}</Heading>
                          </Flex>

                          <ChecksBar variant={checksColor[key]}></ChecksBar>

                          {check?.map(
                            ({ id, name, status, title, updated_at }, i) => (
                              <CheckSection
                                key={i}
                                title={title}
                                status={status}
                                id={id}
                                name={name}
                                date={updated_at}
                                useSlider={true}
                              />
                            ),
                          )}
                        </Box>
                      ),
                    )}
                </Flex>
              </Box>
            </Flex>
          </Container>
          <Comments
            isOpen={isCommentsOpen}
            onRequestClose={() => setIsCommentsOpen(false)}
            referenceId={individual?.id}
            commentType="Individual"
          />
          <AdditionalDocuments
            isOpen={isViewDocumentsOpen}
            onRequestClose={() => setIsViewDocumentsOpen(false)}
            documents={additionalDocuments}
            documentsLoading={documentsLoading}
            includedAdditionalDocument={additionalDocumentsData}
            mandateId={individual?.mandate}
            individualId={individual?.id}
            documentType={formRequestType}
          />
          <IndividualDetailsView
            isOpen={isAllDetailsOpen}
            onRequestClose={() => setIsAllDetailsOpen(false)}
          />
          {additionalDocumentsData && (
            <NewDocumentsRequest
              isOpen={isSelectAdditionalDocumentOpen}
              onRequestClose={() => setIsSelectAdditionalDocumentOpen(false)}
              includedAdditionalDocument={additionalDocumentsData}
              mandateId={individual?.mandate}
              individualId={individual?.id}
              documentType={formRequestType}
            />
          )}

          {formsData && (
            <MandateFormList
              isOpen={showAddNewForm}
              onRequestClose={handleClose}
              mandateId={individual?.mandate}
              individualId={individual?.id}
              includedForms={formsData}
              type={formRequestType}
            />
          )}

          <Modal
            isOpen={isConfirmDeleteOpen}
            onClose={() => setIsConfirmDeleteOpen(false)}
            onModalConfirmClick={handleConfirmDeleteIndividual}
            title={
              formRequestType === "entity"
                ? "Do you want to delete this application?"
                : "Do you want to delete individual?"
            }
            content="This is an irreversible action, if you delete it you will not be able to undo the change. "
            hasSecondaryButton
            secondaryButtonText="Cancel"
            width="450px"
            primaryButtonText="Delete"
            primaryButtonVariant="danger"
          />
          <Modal
            isClosingDisabled={isLoadingReset}
            isLoadingAction={isLoadingReset}
            loadingActionText="Resetting..."
            isOpen={isResetModalOpen}
            onClose={() => setIsResetModalOpen(false)}
            title="Reset individual?"
            content="Resetting this individual will permanently delete all provided information and previous check results. They will receive a new invite code and must restart the verification process from the beginning."
            onModalConfirmClick={() => handleResetIndividual()}
            secondaryButtonText="Cancel"
            primaryButtonText="Reset"
            primaryButtonVariant="danger"
            hasSecondaryButton
            width="450px"
          />
        </>
      )}
    </div>
  );
};
